// ============================================================================
// MON COMPTE — les règles de l'écran, sans rendu : où en est la saisie d'un mot de passe, et quel appareil porte une
// session.
//
// ⚠️ LA LONGUEUR EXIGÉE VIENT DU SERVEUR (`/auth/security`), jamais d'un nombre écrit ici : l'écran annonce la règle que
//    le serveur appliquera. La liste des mots trop courants, elle, reste au serveur — un refus porte alors son motif.
// ⚠️ L'APPAREIL SE LIT DANS L'EN-TÊTE DU NAVIGATEUR, qui ne dit rien de sûr : on en tire un nom lisible quand il est
//    reconnaissable, et `null` sinon — l'écran dit alors « navigateur inconnu » plutôt que d'en deviner un.
// ============================================================================

/** Ce que la saisie permet, dans l'ordre où l'utilisateur la remplit. */
export type PasswordEntry =
  | { kind: 'empty' }
  | { kind: 'short'; missing: number }
  | { kind: 'mismatch' }
  | { kind: 'needCurrent' }
  | { kind: 'ready' };

/**
 * OÙ EN EST LE CHANGEMENT DE MOT DE PASSE — ce qui manque encore, une chose à la fois, dans l'ordre de la saisie.
 * ⚠️ « Prêt » seulement quand les trois champs le sont : le bouton ne s'allume pas avant, plutôt que d'envoyer une
 *    demande que le serveur refusera.
 */
export function passwordEntry(input: { current: string; next: string; confirm: string }, minLength: number): PasswordEntry {
  if (input.next.length === 0) return { kind: 'empty' };
  if (input.next.length < minLength) return { kind: 'short', missing: minLength - input.next.length };
  if (input.next !== input.confirm) return { kind: 'mismatch' };
  if (input.current.length === 0) return { kind: 'needCurrent' };
  return { kind: 'ready' };
}

/** Le genre d'appareil d'une session — il choisit le pictogramme. */
export type SessionDevice = 'desktop' | 'mobile';

/** Ce qu'on sait dire d'un appareil : un navigateur et un système nommés, ou rien de sûr. */
export interface AgentView {
  browser: string | null;
  system: string | null;
  device: SessionDevice;
}

// ⚠️ DES NOMS DE MARQUE, pas des textes : ils s'écrivent ainsi dans toutes les langues (décision écrite dans
//    `scripts/i18n-zones.mjs`). L'ORDRE COMPTE — Edge et Opera se déclarent AUSSI « Chrome », Chrome se déclare AUSSI
//    « Safari » : le premier jeton reconnu l'emporte.
const BROWSERS: readonly (readonly [RegExp, string])[] = [
  [/\bEdg(e|A|iOS)?\//, 'Edge'],
  [/\b(OPR|Opera)\//, 'Opera'],
  [/\b(Firefox|FxiOS)\//, 'Firefox'],
  [/\b(Chrome|CriOS|Chromium)\//, 'Chrome'],
  [/\bVersion\/[\d.]+.*\bSafari\//, 'Safari'],
];
const SYSTEMS: readonly (readonly [RegExp, string])[] = [
  [/\b(iPhone|iPad|iPod)\b/, 'iOS'],
  [/\bAndroid\b/, 'Android'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\bWindows\b/, 'Windows'],
  [/\bMac OS X\b|\bMacintosh\b/, 'macOS'],
  [/\bLinux\b/, 'Linux'],
];

/** CE QUE L'EN-TÊTE DU NAVIGATEUR DIT D'UNE SESSION — navigateur, système, et s'il s'agit d'un appareil mobile. */
export function describeAgent(userAgent: string | null): AgentView {
  const ua = userAgent ?? '';
  const pick = (table: readonly (readonly [RegExp, string])[]): string | null => table.find(([re]) => re.test(ua))?.[1] ?? null;
  const mobile = /\b(Mobile|iPhone|iPad|iPod|Android|Tablet)\b/.test(ua);
  return { browser: pick(BROWSERS), system: pick(SYSTEMS), device: mobile ? 'mobile' : 'desktop' };
}
